class LandingBenefitCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.viewport = this.querySelector('.landing-benefits__viewport');
    this.slides = [...this.querySelectorAll('.landing-benefits__slide')];
    this.dots = [...this.querySelectorAll('[data-slide]')];
    this.index = 0;
    this.paused = false;
    this.renderPlayback();
    this.visible = false;
    this.hovered = false;
    this.interacting = false;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.querySelector('[data-next]')?.addEventListener('click', () => this.select(this.index + 1), options);
    this.querySelector('[data-previous]')?.addEventListener('click', () => this.select(this.index - 1), options);
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.select(index), options));
    this.querySelector('[data-pause]')?.addEventListener(
      'click',
      () => {
        this.paused = !this.paused;
        this.renderPlayback();
        this.schedule();
      },
      options
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      },
      options
    );
    this.viewport.addEventListener(
      'scroll',
      () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          this.index = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
          this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
          this.schedule();
        }, 120);
      },
      options
    );
    this.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.schedule();
      },
      options
    );
    this.addEventListener(
      'pointerleave',
      () => {
        this.hovered = false;
        this.schedule();
      },
      options
    );
    this.addEventListener(
      'pointerdown',
      () => {
        this.interacting = true;
        this.schedule();
      },
      options
    );
    const release = () => {
      this.interacting = false;
      this.schedule();
    };
    window.addEventListener('pointerup', release, options);
    window.addEventListener('pointercancel', release, options);
    this.addEventListener('focusin', () => this.schedule(), options);
    this.addEventListener('focusout', () => setTimeout(() => this.schedule(), 0), options);
    document.addEventListener('visibilitychange', () => this.schedule(), options);
    this.motion.addEventListener('change', () => this.schedule(), options);
    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        this.paused = true;
        this.renderPlayback();
        if (index >= 0) this.select(index);
        else this.schedule();
      },
      options
    );
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
        this.schedule();
      },
      { threshold: 0.35 }
    );
    this.observer.observe(this);
    this.resizeObserver = new ResizeObserver(() => {
      this.viewport.scrollTo({ left: this.index * this.viewport.clientWidth, behavior: 'instant' });
    });
    this.resizeObserver.observe(this.viewport);
  }

  renderPlayback() {
    const button = this.querySelector('[data-pause]');
    if (!button) return;
    const label = this.paused ? 'Start slideshow' : 'Pause slideshow';
    button.querySelector('[data-playback-label]').textContent = label;
    button.querySelector('[data-play-icon]').hidden = !this.paused;
    button.querySelector('[data-pause-icon]').hidden = this.paused;
    button.setAttribute('aria-label', label);
  }

  select(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.viewport.scrollTo({
      left: this.index * this.viewport.clientWidth,
      behavior: this.motion.matches ? 'instant' : 'smooth',
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
    this.schedule();
  }

  schedule() {
    clearTimeout(this.timer);
    if (
      !this.isConnected ||
      this.slides.length < 2 ||
      this.paused ||
      this.hovered ||
      this.interacting ||
      !this.visible ||
      document.hidden ||
      this.motion.matches ||
      this.contains(document.activeElement)
    )
      return;
    this.timer = setTimeout(() => this.select(this.index + 1), Number(this.dataset.interval) || 6000);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.timer);
    clearTimeout(this.scrollTimer);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('landing-benefit-carousel'))
  customElements.define('landing-benefit-carousel', LandingBenefitCarousel);
